import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Fab,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';
import SendIcon from '@mui/icons-material/Send';
import PublicLayout from '../layouts/PublicLayout';

function ContactPage() {
  const theme = useTheme();
  const [contact, setContact] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const mqSm = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setLoading(true);
    api.get('/api/contact')
      .then(({ data }) => setContact(data || {}))
      .catch(() => setContact({}))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    setSent(false);
    try {
      // Open user's mail client with pre-filled message to info@timte.co.tz
      const to = 'info@timte.co.tz';
      const subject = encodeURIComponent(form.subject || `Message from TIMTE 2026 website: ${form.name || 'Visitor'}`);
      const bodyLines = [
        `Name: ${form.name || ''}`,
        `Email: ${form.email || ''}`,
        `Subject: ${form.subject || ''}`,
        '',
        `${form.message || ''}`,
        '',
        'Sent from TIMTE 2026 website'
      ];
      const body = encodeURIComponent(bodyLines.join('\n'));
      const mailto = `mailto:${to}?subject=${subject}&body=${body}`;
      window.location.href = mailto;

      // Show a friendly themed confirmation dialog and snackbar
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '' });
      setSnack({ open: true, severity: 'success', message: 'Message prepared — your email client opened.' });
      setDialogOpen(true);
    } catch (err) {
      setError('Failed to launch email client. Please email info@timte.co.tz manually.');
      setSnack({ open: true, severity: 'error', message: 'Send failed' });
    } finally {
      setSending(false);
    }
  };

  const parsedSocial = (() => {
    if (!contact) return [];
    if (Array.isArray(contact.social)) return contact.social.map(s => ({ platform: (s.platform || '').toLowerCase(), url: s.url || s }));
    if (typeof contact.social === 'string') {
      return contact.social.split(',').map(s => {
        const parts = s.split(':').map(p => p.trim());
        return parts.length >= 2 ? { platform: parts[0].toLowerCase(), url: parts.slice(1).join(':') } : { platform: parts[0].toLowerCase(), url: '' };
      });
    }
    return [];
  })();

  // If platform not provided, try to infer from URL
  const normalizedSocial = parsedSocial.map(s => {
    if (s.platform) return s;
    const url = (s.url || '').toLowerCase();
    if (url.includes('twitter.com') || url.includes('x.com')) return { platform: 'twitter', url: s.url };
    if (url.includes('facebook.com')) return { platform: 'facebook', url: s.url };
    if (url.includes('instagram.com')) return { platform: 'instagram', url: s.url };
    if (url.includes('linkedin.com')) return { platform: 'linkedin', url: s.url };
    if (url) return { platform: 'website', url: s.url };
    return s;
  });

  const getSocialUrl = (platform) => normalizedSocial.find(p => (p.platform || '').includes(platform))?.url;

  const whatsappLink = () => {
    const w = parsedSocial.find(p => p.platform.includes('whatsapp'))?.url;
    if (w) return w.startsWith('http') ? w : `https://wa.me/${w.replace(/[^0-9]/g, '')}`;
    if (contact.phone) return `https://wa.me/${String(contact.phone).replace(/[^0-9]/g, '')}`;
    return null;
  };

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setSnack({ open: true, severity: 'success', message: 'Copied to clipboard' });
    } catch {
      setSnack({ open: true, severity: 'error', message: 'Copy failed' });
    }
  };

  const renderMap = (embed) => {
    if (!embed) return null;
    const trimmed = String(embed).trim();
    if (trimmed.startsWith('<iframe') || trimmed.includes('<iframe')) {
      return <div dangerouslySetInnerHTML={{ __html: trimmed }} />;
    }
    if (/google\.com\/maps/.test(trimmed)) {
      const src = trimmed.includes('embed') ? trimmed : trimmed.replace('/place/', '/embed?');
      return <iframe title="map" src={src} style={{ width: '100%', height: 360, border: 0 }} />;
    }
    return <Box><a href={trimmed} target="_blank" rel="noreferrer">Open map link</a></Box>;
  };

  return (
    <PublicLayout>
      <Box sx={{
        width: '100%',
        minHeight: '100vh',
        position: 'relative',
        backgroundImage: contact.backgroundImage ? `url(${contact.backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{
            backgroundColor: contact.backgroundImage
              ? (theme.palette.mode === 'dark' ? 'rgba(8,8,10,0.52)' : 'rgba(255,255,255,0.62)')
              : (theme.palette.mode === 'dark' ? 'rgba(14,14,14,0.92)' : '#fff'),
            borderRadius: 2,
            p: { xs: 2, md: 4 },
            boxShadow: contact.backgroundImage ? 6 : 1,
            border: contact.backgroundImage ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.04)'}`,
            backdropFilter: contact.backgroundImage ? 'blur(6px)' : 'none',
            WebkitBackdropFilter: contact.backgroundImage ? 'blur(6px)' : 'none'
          }}>
            <Typography variant="h3" gutterBottom fontWeight={700} align="center">Contact Us</Typography>
            <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 4, maxWidth: 900, mx: 'auto' }}>{contact.intro || 'We would love to hear from you. Use the form or contact options below.'}</Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      boxShadow: contact.backgroundImage ? 3 : 1,
                      backgroundColor: contact.backgroundImage
                        ? (theme.palette.mode === 'dark' ? 'rgba(6,6,6,0.36)' : 'rgba(255,255,255,0.44)')
                        : (theme.palette.mode === 'dark' ? 'rgba(20,20,20,0.9)' : '#fff'),
                      border: contact.backgroundImage ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none',
                      backdropFilter: contact.backgroundImage ? 'blur(6px)' : 'none'
                    }}
                    elevation={1}
                    component="section"
                  >
                    <Typography variant="h6" gutterBottom>Contact Details</Typography>
                    <Stack spacing={1}>
                      {contact.address && <Box><Typography variant="body2"><strong>Address:</strong> {contact.address}</Typography></Box>}

                      {contact.phone && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <PhoneIcon fontSize="small" />
                          <Typography variant="body2">{contact.phone}</Typography>
                          <Button size="small" onClick={() => window.open(`tel:${contact.phone}`)}>Call</Button>
                          <IconButton size="small" onClick={() => handleCopy(contact.phone)}><ContentCopyIcon fontSize="small" /></IconButton>
                        </Box>
                      )}

                      {contact.email && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <EmailIcon fontSize="small" />
                          <Typography variant="body2"><a href={`mailto:${contact.email}`}>{contact.email}</a></Typography>
                          <IconButton size="small" onClick={() => handleCopy(contact.email)}><ContentCopyIcon fontSize="small" /></IconButton>
                        </Box>
                      )}

                      {contact.contactReceiver && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <EmailIcon fontSize="small" />
                          <Typography variant="body2"><strong>Communications:</strong> <a href={`mailto:${contact.contactReceiver}`}>{contact.contactReceiver}</a></Typography>
                          <IconButton size="small" onClick={() => handleCopy(contact.contactReceiver)}><ContentCopyIcon fontSize="small" /></IconButton>
                        </Box>
                      )}

                      {contact.extra && <Typography variant="body2">{contact.extra}</Typography>}

                      {contact.mapUrl && <Box sx={{ mt: 2 }}><iframe title="map" src={contact.mapUrl} width="100%" height={180} style={{ border: 0, borderRadius: 8 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></Box>}

                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ mt: 1 }}>Follow us</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            {getSocialUrl('instagram') ? <Tooltip title="Instagram"><IconButton component="a" href={getSocialUrl('instagram')} target="_blank"><InstagramIcon /></IconButton></Tooltip> : null}
                            {getSocialUrl('facebook') ? <Tooltip title="Facebook"><IconButton component="a" href={getSocialUrl('facebook')} target="_blank"><FacebookIcon /></IconButton></Tooltip> : null}
                            {getSocialUrl('x') || getSocialUrl('twitter') ? <Tooltip title="X / Twitter"><IconButton component="a" href={getSocialUrl('x') || getSocialUrl('twitter')} target="_blank"><TwitterIcon /></IconButton></Tooltip> : null}
                            {getSocialUrl('linkedin') ? <Tooltip title="LinkedIn"><IconButton component="a" href={getSocialUrl('linkedin')} target="_blank"><LinkedInIcon /></IconButton></Tooltip> : null}
                            {whatsappLink() ? <Tooltip title="WhatsApp"><IconButton component="a" href={whatsappLink()} target="_blank"><WhatsAppIcon color="success" /></IconButton></Tooltip> : null}
                            {getSocialUrl('website') ? <Tooltip title="Website"><IconButton component="a" href={getSocialUrl('website')} target="_blank"><LanguageIcon /></IconButton></Tooltip> : null}
                          </Box>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2">Quick actions</Typography>
                          <Stack direction={mqSm ? 'column' : 'row'} spacing={1} sx={{ mt: 1 }}>
                            {contact.phone && <Button fullWidth={mqSm} variant="outlined" startIcon={<PhoneIcon />} onClick={() => window.open(`tel:${contact.phone}`)}>Call</Button>}
                            {whatsappLink() && <Button fullWidth={mqSm} variant="contained" startIcon={<WhatsAppIcon />} color="success" onClick={() => window.open(whatsappLink(), '_blank')}>WhatsApp</Button>}
                            {contact.email && <Button fullWidth={mqSm} variant="outlined" startIcon={<EmailIcon />} onClick={() => window.open(`mailto:${contact.email}`)}>Email</Button>}
                          </Stack>
                        </Box>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 3, boxShadow: contact.backgroundImage ? 3 : 1, backgroundColor: contact.backgroundImage ? (theme.palette.mode === 'dark' ? 'rgba(6,6,6,0.32)' : 'rgba(255,255,255,0.44)') : (theme.palette.mode === 'dark' ? 'rgba(20,20,20,0.9)' : '#fff'), border: contact.backgroundImage ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none', backdropFilter: contact.backgroundImage ? 'blur(6px)' : 'none' }} elevation={1} component="section">
                    <Typography variant="h6" gutterBottom>Send us a message</Typography>
                    {contact.formEnabled ? (
                      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField label="Your name" name="name" required value={form.name} onChange={handleChange} />
                        <TextField label="Your email" name="email" required type="email" value={form.email} onChange={handleChange} />
                        <TextField label="Subject" name="subject" value={form.subject} onChange={handleChange} />
                        <TextField label="Message" name="message" value={form.message} onChange={handleChange} multiline minRows={4} />
                        <Button type="submit" variant="contained" disabled={sending}>{sending ? 'Preparing email…' : 'Send Message'}</Button>
                        {error && <Alert severity="error">{error}</Alert>}
                        {sent && <Alert severity="success">Message prepared in your email client.</Alert>}
                      </Box>
                    ) : (
                      <Typography variant="body2">Contact form is currently disabled. Please email or call instead.</Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 3, borderRadius: 3, boxShadow: contact.backgroundImage ? 3 : 1, backgroundColor: contact.backgroundImage ? (theme.palette.mode === 'dark' ? 'rgba(6,6,6,0.32)' : 'rgba(255,255,255,0.44)') : (theme.palette.mode === 'dark' ? 'rgba(20,20,20,0.9)' : '#fff'), border: contact.backgroundImage ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none', backdropFilter: contact.backgroundImage ? 'blur(6px)' : 'none' }} elevation={1}>
                    <Typography variant="h6" gutterBottom>Location</Typography>
                    {renderMap(contact.mapEmbed)}
                  </Paper>
                </Grid>
              </Grid>
            )}

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
              <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
            </Snackbar>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
              <DialogTitle sx={{ bgcolor: 'primary.main', color: '#fff', fontWeight: 900 }}>Welcome to TIMTE 2026</DialogTitle>
              <DialogContent sx={{ minWidth: 320 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>We received your message and your email client opened to send it to <strong>info@timte.co.tz</strong>.</Typography>
                <Typography variant="body2" color="text.secondary">Thank you — we will contact you back shortly.</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogOpen(false)} variant="contained" sx={{ bgcolor: 'primary.main', color: '#fff' }}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Global WhatsApp FAB moved to layout; local FAB removed to avoid duplication */}
          </Box>
        </Container>
      </Box>
    </PublicLayout>
  );
}

export default ContactPage;
